import {noop} from '@benchling/js-utils';
import {ArgumentNode, Kind, ObjectFieldNode, print, ValueNode} from 'query-api-graphql';
import React from 'react';
import _ from 'underscore';

import {
  ArchiveStatus,
  ALL_ARCHIVED_PURPOSE_STRING,
  CAPILLARY_ANY_ARCHIVED_PURPOSE_STRING,
} from 'archive/ArchiveConstants';
import {getPurposesForFolderSection} from 'archive/ArchivePurposesUtil';
import {assertAndContinue} from 'Asserts';
import {FolderSection} from 'constants/FolderConstants';
import type {FilterOption} from 'search/filters/base/FilterComponents';
import FiltersContainer, {useSecondaryDropdownState} from 'search/filters/FiltersContainer';
import {Filter, FilterKey, GenericFilter, keyToEmptyFilter} from 'search/SearchConstants';
import removeNulls from 'util/removeNulls';
import {assertNever} from 'util/Types';

/**
 * List of Query API specific filter keys. Ordered such that keys on top will show up at the top of the
 * dropdown to select the filter key.
 *
 * @package
 */
export const QUERY_API_FILTER_KEYS = [
  FilterKey.QUERY_API_BIOENTITY_ID,
  FilterKey.QUERY_API_CREATED_AT,
  FilterKey.QUERY_API_MODIFIED_AT,
  FilterKey.QUERY_API_AUTHOR,
  FilterKey.QUERY_API_CREATOR,
  FilterKey.QUERY_API_ARCHIVE_REASON,
  FilterKey.QUERY_API_PROJECT,
  FilterKey.QUERY_API_REGISTRY,
] as const;
type QueryApiFilterKey = (typeof QUERY_API_FILTER_KEYS)[number];
export function isQueryApiFilterKey(key: FilterKey): key is QueryApiFilterKey {
  return QUERY_API_FILTER_KEYS.includes(key as QueryApiFilterKey);
}

/**
 * The values correspond to argument names in the Query API on entity listing fields.
 *
 * For example, `org__antibodyItemsPreview(modifiedAt: DateTimeFilter)` -> "modifiedAt"
 */
export const FILTER_KEY_TO_QUERY_API_ARG_NAME: {[key in QueryApiFilterKey]: string} = {
  [FilterKey.QUERY_API_BIOENTITY_ID]: 'id',
  [FilterKey.QUERY_API_MODIFIED_AT]: 'modifiedAt',
  [FilterKey.QUERY_API_CREATED_AT]: 'createdAt',
  [FilterKey.QUERY_API_AUTHOR]: 'authors',
  [FilterKey.QUERY_API_CREATOR]: 'creator',
  [FilterKey.QUERY_API_ARCHIVE_REASON]: 'archiveReason',
  [FilterKey.QUERY_API_PROJECT]: 'project',
  [FilterKey.QUERY_API_REGISTRY]: 'registry',
};

/**
 * @package
 */
export const QUERY_API_ARG_NAME_TO_FILTER_KEY: {[key in string]: QueryApiFilterKey} = _.invert(
  FILTER_KEY_TO_QUERY_API_ARG_NAME
);

/**
 * Return the string value associated with the input `ObjectFieldNode`, or `null` if the `ObjectFieldNode`'s
 * value is not of string-type.
 *
 * Also `assertAndContinues` that the value is of string-type, so this should only be
 * used when you expect that to be the case.
 */
function fieldNodeToStringValue(field: ObjectFieldNode): string | null {
  if (field.value.kind !== Kind.STRING) {
    assertAndContinue(false, 'Expected field.value to be STRING type', {
      valueKind: field.value.kind,
      name: field.name.value,
    });
    return null;
  }
  return field.value.value;
}

export function graphQLArgumentsToFilters(gqlArguments: Array<ArgumentNode>): Array<Filter> {
  return removeNulls(
    gqlArguments.map((gqlArgument) => {
      const filterKey = QUERY_API_ARG_NAME_TO_FILTER_KEY[gqlArgument.name.value];
      if (filterKey === undefined) {
        assertAndContinue(false, 'Got unexpected GQL argument', {
          argument: print(gqlArgument),
        });
        return null;
      }

      if (gqlArgument.value.kind !== Kind.OBJECT) {
        // Null values are possible in the Query API schema but we shouldn't be constructing them; elliding
        // them with an error is sufficient for now.
        assertAndContinue(false, 'Expected all filter arguments to be objects', {
          argument: print(gqlArgument),
        });
        return null;
      }

      if (filterKey === FilterKey.QUERY_API_MODIFIED_AT || filterKey === FilterKey.QUERY_API_CREATED_AT) {
        const gteField = gqlArgument.value.fields.find((field) => field.name.value === 'gte');
        const lteField = gqlArgument.value.fields.find((field) => field.name.value === 'lte');

        return {
          ...keyToEmptyFilter(filterKey),
          value: {
            fromDatetime: gteField == null ? null : fieldNodeToStringValue(gteField),
            toDatetime: lteField == null ? null : fieldNodeToStringValue(lteField),
          },
        };
      } else if (
        filterKey === FilterKey.QUERY_API_CREATOR ||
        filterKey === FilterKey.QUERY_API_AUTHOR ||
        filterKey === FilterKey.QUERY_API_BIOENTITY_ID ||
        filterKey === FilterKey.QUERY_API_ARCHIVE_REASON
      ) {
        const anyOfField = gqlArgument.value.fields.find((field) => field.name.value === 'anyOf');
        if (anyOfField == null) {
          return null;
        }
        if (anyOfField.value.kind !== Kind.LIST) {
          assertAndContinue(false, 'Got anyOf filter with non-list value', {
            argument: print(gqlArgument),
          });
          return null;
        }

        return {
          ...keyToEmptyFilter(filterKey),
          value: removeNulls(
            anyOfField.value.values.map((value) => {
              if (value.kind !== Kind.STRING) {
                assertAndContinue(false, 'Got anyOf filter with non-string array member', {
                  argument: print(gqlArgument),
                });
                return null;
              }
              return value.value;
            })
          ),
        };
      } else if (filterKey === FilterKey.QUERY_API_PROJECT || filterKey === FilterKey.QUERY_API_REGISTRY) {
        const eqField = gqlArgument.value.fields.find((field) => field.name.value === 'eq');
        if (eqField == null) {
          return keyToEmptyFilter(filterKey);
        }
        if (eqField.value.kind !== Kind.STRING) {
          assertAndContinue(false, 'Got eq filter with non-string value', {
            argument: print(gqlArgument),
          });
          return null;
        }

        return {
          ...keyToEmptyFilter(filterKey),
          value: eqField.value.value,
        };
      }

      assertAndContinue(false, 'Got unsupported filter key', {filterKey: assertNever(filterKey)});
      return null;
    })
  );
}

function buildDatetimeFilterValue(ltFieldValue: string | null, gtFieldValue: string | null): ValueNode {
  return {
    kind: Kind.OBJECT,
    fields: removeNulls([
      ltFieldValue == null
        ? null
        : {
            kind: Kind.OBJECT_FIELD,
            name: {kind: Kind.NAME, value: 'lte'},
            value: {kind: Kind.STRING, value: ltFieldValue},
          },
      gtFieldValue == null
        ? null
        : {
            kind: Kind.OBJECT_FIELD,
            name: {kind: Kind.NAME, value: 'gte'},
            value: {kind: Kind.STRING, value: gtFieldValue},
          },
    ]),
  };
}

export function buildAnyOfFilterValue(values: Array<string>): ValueNode {
  return {
    kind: Kind.OBJECT,
    fields: [
      {
        kind: Kind.OBJECT_FIELD,
        name: {kind: Kind.NAME, value: 'anyOf'},
        value: {kind: Kind.LIST, values: values.map((value) => ({kind: Kind.STRING, value}))},
      },
    ],
  };
}

function buildEqFilterValue(value: string): ValueNode {
  return {
    kind: Kind.OBJECT,
    fields: [
      {
        kind: Kind.OBJECT_FIELD,
        name: {kind: Kind.NAME, value: 'eq'},
        value: {kind: Kind.STRING, value},
      },
    ],
  };
}

function buildArgument(name: string, value: ValueNode): ArgumentNode {
  return {
    kind: Kind.ARGUMENT,
    name: {kind: Kind.NAME, value: name},
    value,
  };
}

const FILTER_KEY_TO_BUILD_ARG_VALUE_FN: {
  [key in QueryApiFilterKey]: (filter: GenericFilter<key>) => ValueNode | null;
} = {
  [FilterKey.QUERY_API_BIOENTITY_ID]: (filter) => buildAnyOfFilterValue(filter.value),
  [FilterKey.QUERY_API_AUTHOR]: (filter) => buildAnyOfFilterValue(filter.value),
  [FilterKey.QUERY_API_CREATOR]: (filter) => buildAnyOfFilterValue(filter.value),
  // Transform 'ALL_ARCHIVED' to 'ANY_ARCHIVED' (which means archived for any reason for the Query API)
  [FilterKey.QUERY_API_ARCHIVE_REASON]: (filter) =>
    buildAnyOfFilterValue(
      filter.value.map((archivePurpose) =>
        archivePurpose === ALL_ARCHIVED_PURPOSE_STRING
          ? CAPILLARY_ANY_ARCHIVED_PURPOSE_STRING
          : archivePurpose
      )
    ),

  [FilterKey.QUERY_API_CREATED_AT]: (filter) =>
    buildDatetimeFilterValue(filter.value.toDatetime, filter.value.fromDatetime),
  [FilterKey.QUERY_API_MODIFIED_AT]: (filter) =>
    buildDatetimeFilterValue(filter.value.toDatetime, filter.value.fromDatetime),

  [FilterKey.QUERY_API_PROJECT]: (filter) => (filter.value == null ? null : buildEqFilterValue(filter.value)),
  [FilterKey.QUERY_API_REGISTRY]: (filter) =>
    filter.value == null ? null : buildEqFilterValue(filter.value),
};

function getBuildArgumentValueFn(filterKey: QueryApiFilterKey): (filter: Filter) => ValueNode | null {
  return FILTER_KEY_TO_BUILD_ARG_VALUE_FN[filterKey];
}

/** @package */
export function filtersToGraphQLArguments(filters: Array<Filter>): Array<ArgumentNode> {
  return removeNulls(
    _.unique(filters, (filter) => filter.key).map((filter) => {
      if (!isQueryApiFilterKey(filter.key)) {
        assertAndContinue(false, 'Got unsupported filter key', {filterKey: filter.key});
        return null;
      }

      const buildArgValueFn = getBuildArgumentValueFn(filter.key);
      const argValue = buildArgValueFn(filter);

      return argValue == null ? null : buildArgument(FILTER_KEY_TO_QUERY_API_ARG_NAME[filter.key], argValue);
    })
  );
}

export function getQueryApiSecondaryFilterOptions(schemaId: string): Array<FilterOption> {
  return QUERY_API_FILTER_KEYS.map((filterKey) =>
    filterKey === FilterKey.QUERY_API_BIOENTITY_ID
      ? {filterKey, extraProps: {schemaId}}
      : filterKey === FilterKey.QUERY_API_ARCHIVE_REASON
        ? {filterKey, extraProps: {archivePurposes: getPurposesForFolderSection(FolderSection.ALL)}}
        : {filterKey}
  );
}

/**
 * Component to render filters for the Query API.
 *
 * Right now this is not based on introspection at all; all filters will be rendered, even if they are not
 * valid for the field node the arguments are interpolated into.
 *
 * TODO(BNCH-57081): Use introspection and `valueFromAST` to help clean this up and make it more robust.
 * In particular, we can determine which arguments exist on the input field using introspection, and have
 * static maps to determine which filters those correspond to and the expected shape of their argument values
 * once they are converted into JS objects using `valueFromAST`.
 *
 * In an ideal world, we'd have a typescript representation of the static part of the SDL on the frontend so
 * that we could be certain of the shape of different filters. In the interim, hardcoding interfaces like
 * `BenchlingDateTimeValue = {lt?: string; gt?: string; lte?: string; gte?: string}` should be sufficient.
 *
 * This should also involve overriding validation for custom scalars such as `BenchlingDateTime` because
 * any value for custom scalars is by default treated as valid. We could investigate using `graphql-tools` to
 * layer in the custom scalar validation, but it's probably better to avoid using a new package if possible.
 * See https://stackoverflow.com/questions/47824603/graphql-custom-scalar-definition-without-graphql-tools for
 * ideas on how to do this. Pseudocode (in useQueryApiIntrospection) could look something like:
 * ```
 * schema = buildClientSchema(...);
 * schema.getType('BenchlingDateTime').parseLiteral = (value: ValueNode) => {
 *   if (value.kind !== Kind.STRING || !isValidDateTimeString(value.value) {
 *     throw new GraphQLError('Got invalid BenchlingDateTime value');
 *   }
 *   return value.value;
 * }
 * schema.getType('BenchlingDateTime').parseValue = (value: any) => {
 *   if (typeof value !== 'string' || !isValidDateTimeString(value)) {
 *     throw new GraphQLError('Got invalid BenchlingDateTime value');
 *   }
 *   return value;
 * }
 * ```
 *
 * Alternatively, we could build a new custom scalar type and override the `BenchlingDateTime` type built
 * from introspection:
 * ```
 * schema = buildClientSchema(...);
 * benchlingDateTimeOverride = new GraphQLScalarType({..., parseValue: seeAbove, parseLiteral: seeAbove});
 * schema._typeMap.BenchlingDateTime = benchlingDateTimeOverride;
 * ```
 */
export default function QueryApiFilters({
  filterArguments,
  onChangeFilterArguments,
  schemaId,
}: {
  filterArguments: Array<ArgumentNode>;
  onChangeFilterArguments: (newFilterArguments: Array<ArgumentNode>) => void;
  schemaId: string;
}): JSX.Element {
  const secondaryDropdownState = useSecondaryDropdownState();
  return (
    <FiltersContainer
      filters={graphQLArgumentsToFilters(filterArguments)}
      onChangeFilters={(newFilters) => {
        onChangeFilterArguments(filtersToGraphQLArguments(newFilters));
      }}
      primaryFilterOptions={[]}
      // TODO(BNCH-57244): Try to reuse logic from `getDefaultSecondaryFilterGroups`
      secondaryFilterProps={{
        filterGroups: [
          {
            filterOptions: getQueryApiSecondaryFilterOptions(schemaId),
          },
        ],
        dropLeft: true,
        ...secondaryDropdownState,
      }}
      selectedSchema={null}
    />
  );
}

/**
 * Render placeholder filter disabled component.
 *
 * Convenience component to handle empty/defaults for all the props.
 */
export function DisabledQueryApiFilters(): JSX.Element {
  const secondaryDropdownState = useSecondaryDropdownState();
  return (
    <FiltersContainer
      disabled={true}
      filters={[]}
      onChangeFilters={noop}
      primaryFilterOptions={[]}
      secondaryFilterProps={{filterGroups: [], ...secondaryDropdownState}}
      selectedSchema={null}
    />
  );
}

export const NOT_ARCHIVED_ARCHIVE_REASON_ARGUMENT = buildArgument(
  FILTER_KEY_TO_QUERY_API_ARG_NAME[FilterKey.QUERY_API_ARCHIVE_REASON],
  buildAnyOfFilterValue([ArchiveStatus.NOT_ARCHIVED])
);

export function buildRegistryArgument(registryId: string): ArgumentNode {
  return buildArgument(
    FILTER_KEY_TO_QUERY_API_ARG_NAME[FilterKey.QUERY_API_REGISTRY],
    buildEqFilterValue(registryId)
  );
}
